import OneSignal from 'react-onesignal';
import { handleNotificationClick } from './notificationRouter';

/**
 * Helper function to create a delay
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise<void>}
 */
const delay = (ms) => {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve();
        }, ms);
    });
};

let _isInitialized = false;

/**
 * Initialize OneSignal SDK. Safe to call multiple times — only runs once per page lifecycle.
 * @param {string} appId - OneSignal App ID
 * @returns {Promise<void>}
 */
export const initializeOneSignal = async (appId) => {
    if (!appId) {
        console.warn('[OneSignal] No App ID provided');
        return;
    }

    if (_isInitialized) return;

    try {
        await OneSignal.init({
            appId,
            allowLocalhostAsSecureOrigin: true,
            serviceWorkerParam: { scope: '/' },
            serviceWorkerPath: 'service-worker.js',
        });

        _isInitialized = true;
        await delay(500);
    } catch (error) {
        console.error('[OneSignal] Initialization failed:', error);
        throw error;
    }
};

/** @returns {boolean} Whether OneSignal has been initialized this session */
export const isOneSignalInitialized = () => _isInitialized;

/**
 * Check if notification permission is granted
 * @returns {Promise<boolean>}
 */
export const checkNotificationPermission = async () => {
    try {
        const permission = await OneSignal.Notifications.permissionNative;
        return permission === 'granted';
    } catch (error) {
        return Notification.permission === 'granted';
    }
};

/**
 * Request notification permission from user
 * @returns {Promise<void>}
 */
export const requestNotificationPermission = async () => {
    try {
        // Try OneSignal slidedown prompt first
        if (OneSignal.Slidedown?.promptPush) {
            try {
                await OneSignal.Slidedown.promptPush();
                return;
            } catch (slidedownError) {
                console.error('[OneSignal] Slidedown failed, trying alternative...', slidedownError);
            }
        }

        // Fallback to OneSignal.Notifications.requestPermission
        try {
            await OneSignal.Notifications.requestPermission();
            return;
        } catch (requestError) {
            console.error('[OneSignal] Permission request failed:', requestError);
        }

        // Final fallback to native browser notification API
        if ('Notification' in window && Notification.permission === 'default') {
            await Notification.requestPermission();
        }
    } catch (error) {
        console.error('[OneSignal] Permission request failed:', error);
    }
};

// Track if handler is already registered to prevent duplicates
let isClickHandlerRegistered = false;

/**
 * Setup notification click event handler
 * Waits for OneSignal to be fully ready before adding listener
 * @returns {Promise<void>}
 */
export const setupNotificationClickHandler = async () => {
    try {
        if (isClickHandlerRegistered) {
            return;
        }

        const isReady = await waitForOneSignalReady(5);

        if (!isReady) {
            console.warn('[OneSignal] Not ready, retrying click handler setup...');
            setTimeout(() => setupNotificationClickHandler(), 2000);
            return;
        }

        if (!OneSignal?.Notifications) {
            console.warn('[OneSignal] Notifications API not available, retrying...');
            setTimeout(() => setupNotificationClickHandler(), 2000);
            return;
        }

        if (typeof OneSignal.Notifications.addEventListener !== 'function') {
            console.error('[OneSignal] addEventListener is not a function');
            return;
        }

        OneSignal.Notifications.addEventListener('click', handleNotificationClick);
        isClickHandlerRegistered = true;
    } catch (error) {
        setTimeout(() => setupNotificationClickHandler(), 2000);
    }
};

/**
 * Check if OneSignal is ready/initialized
 * @returns {Promise<boolean>}
 */
const isOneSignalReady = async () => {
    try {
        // Check if OneSignal object exists and has login method
        if (!OneSignal || typeof OneSignal.login !== 'function') {
            return false;
        }

        // Additional check: verify OneSignal is fully initialized
        // Wait a small delay to ensure OneSignal is ready (especially on mobile)
        await delay(100);

        return true;
    } catch (error) {
        return false;
    }
};

/**
 * Check if user is already logged in to OneSignal with the same userId
 * @param {string|number} userId - User ID to check
 * @returns {Promise<boolean>}
 */
const isAlreadyLoggedIn = async (userId) => {
    try {
        const isReady = await isOneSignalReady();
        if (!isReady) {
            return false;
        }

        const externalId = await OneSignal.User.getExternalId();
        return externalId === String(userId);
    } catch (error) {
        return false;
    }
};

/**
 * Wait for OneSignal to be ready with retries
 * @param {number} remainingAttempts - Remaining retry attempts
 * @param {number} attemptNumber - Current attempt number (for exponential backoff)
 * @returns {Promise<boolean>}
 */
const waitForOneSignalReady = async (remainingAttempts, attemptNumber = 1) => {
    if (remainingAttempts <= 0) {
        return false;
    }

    const isReady = await isOneSignalReady();
    if (isReady) {
        return true;
    }

    const waitTime = Math.min(200 * attemptNumber, 2000);
    await delay(waitTime);

    return waitForOneSignalReady(remainingAttempts - 1, attemptNumber + 1);
};

/**
 * Login user to OneSignal by setting external_id
 * @param {string|number} userId - User ID to set as external_id
 * @param {number} retries - Number of retry attempts (default: 5)
 * @param {boolean} forceLogin - Force login even if already logged in (default: false)
 * @returns {Promise<void>}
 */
export const oneSignalLogin = async (userId, retries = 5, forceLogin = false) => {
    try {
        if (!userId) {
            return;
        }

        const isReady = await waitForOneSignalReady(retries);

        if (!isReady) {
            console.warn('OneSignal is not ready after waiting, skipping login');
            return;
        }

        if (!forceLogin) {
            const alreadyLoggedIn = await isAlreadyLoggedIn(userId);
            if (alreadyLoggedIn) {
                console.warn('OneSignal: Already logged in with userId:', userId);
                return;
            }
        }

        OneSignal.login(String(userId));

        try {
            await delay(200);
        } catch (verifyError) {
            // Ignore verification errors
        }
    } catch (error) {
        console.error('Failed to login OneSignal:', error);

        if (retries > 0) {
            const errorMessage = error?.message || String(error);
            const isTransientError =
                errorMessage.includes('not ready') ||
                errorMessage.includes('not initialized') ||
                errorMessage.includes('timeout') ||
                errorMessage.includes('claimed by another');

            if (isTransientError) {
                await delay(1000);
                await oneSignalLogin(userId, retries - 1, forceLogin);
            }
        }
    }
};

/**
 * Logout user from OneSignal by removing external_id
 * @returns {Promise<void>}
 */
export const oneSignalLogout = async () => {
    try {
        const isReady = await isOneSignalReady();
        if (!isReady) {
            console.warn('OneSignal is not ready, skipping logout');
            return;
        }

        await OneSignal.logout();
    } catch (error) {
        console.error('Failed to logout OneSignal:', error);
    }
};
